import React, { useState, memo, useCallback } from 'react';
import Button from '@mui/material/Button';
import { useImmer } from 'use-immer';

import CustomerSelect, { isValidCustomer } from './CustomerSelect';
import CustomerInvoiceRows, { isValidCustomerInvoiceRows, customerInvoiceRowConstructor } from './CustomerInvoiceRows';
import TextAreaInput from './TextAreaInput';
import ConfirmDialog from './ConfirmDialog';

import formatCurrency, { sumKronor } from './Formatting';
import { CostCenterItem, ProjectWithArticles } from './Types'
import { apiCreateCustomerInvoice, apiGetIncomesData } from './Api';
import { addToCallChain } from './CallCenter';
import { useApiData, LoadError, Loading } from './useApiData';


const nullToZero = (num: number | null): number => num !== null ? num : 0

const customerSelectSx = { width: '40rem' }
const textAreaSx = { width: '30rem' }

const IncomeCreator = memo(({onUpdate}: {onUpdate: () => void}) => {
    const { data: incomeData, error, setData: setIncomeData, reload } = useApiData(apiGetIncomesData, [])
    const [customerId, setCustomerId] = useState<string | null>(null)
    const [rows, setRows] = useImmer([customerInvoiceRowConstructor()])
    const [finalText, setFinalText] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)

    const setCostCenterItems = useCallback((newCostCenterItems: CostCenterItem<ProjectWithArticles>[]) => {
        setIncomeData(data => {if (data !== null) data.cost_center_items = newCostCenterItems})
    }, [setIncomeData])

    if (error !== null) {
        return <LoadError onRetry={reload}/>
    }
    if (incomeData === null) {
        return <Loading/>
    }

    
    const isValid = isValidCustomer(customerId) && isValidCustomerInvoiceRows(rows)

    function createCustomerInvoice() {
        if (!isValid) return
        const data = {'customer_id': customerId, 'rows': rows, 'final_text': finalText}
        addToCallChain(
            async () => {
                await apiCreateCustomerInvoice(data)
                onUpdate()
                console.log("Skapade kundfaktura.")
            },
            true
        )
    }


    const total_amount = sumKronor(rows.map(row => nullToZero(row.quantity) * nullToZero(row.unitPrice)))
    const customerName = incomeData.customers.find(c => c.Id === customerId)?.Name ?? 'kunden'
    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: '0.5rem' }}>
            <CustomerSelect sx={customerSelectSx} label='Medlem/kund' customers={incomeData.customers} value={customerId} onChange={setCustomerId}/>
            <CustomerInvoiceRows costCenterItems={incomeData.cost_center_items} rows={rows} onChangeRows={setRows} onChangeCostCenterItems={setCostCenterItems} officialIncomeAccounts={incomeData.official_income_accounts}/>
            <div style={{ alignSelf: 'stretch', display: 'flex', flexFlow: 'row nowrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <TextAreaInput
                    sx={textAreaSx}
                    label='Fritext'
                    value={finalText}
                    onChange={setFinalText}
                />
                <div>Totalbelopp: {formatCurrency(total_amount)}</div>
            </div>
            <Button variant="contained" id="button-submit-main" disabled={!isValid} onClick={() => setConfirmOpen(true)}>Skapa faktura…</Button>
            <ConfirmDialog
                open={confirmOpen}
                title="Skapa och skicka kundfaktura?"
                message={`En kundfaktura på ${formatCurrency(total_amount)} kr skapas i Visma och skickas via e-post till ${customerName}.`}
                confirmLabel="Skapa och skicka"
                onCancel={() => setConfirmOpen(false)}
                onConfirm={() => { setConfirmOpen(false); createCustomerInvoice() }}
            />
        </div>
    )
})


export default IncomeCreator
